import {useObservable} from "../../../../rxsupport";
import {useForm} from "react-hook-form";
import {useCallback} from "react";
import { showToast } from "common";
import {Subject} from "rxjs";
import {ReportModal} from "../../form-components/ReportModal";
import {
    AuthorizationInfo,
    ClientReportSubmitClinicalRecommendationHourInput,
    defaultClinicalRecommendationHoursFromAuth,
    defaultServiceTypesByAuthTier1,
    isAssessmentAuth,
    isTreatmentAuth,
    serviceTypeOptionsAvailableForAllTiers
} from "../../domain";
import {useLatestClientReturnsTransferInput} from "./useLatestClientReturnsTransferInput";
import {ClientReturnsTransferForm} from "./ClientReturnsTransferForm";
import React, { useMemo } from "react";
import { ProviderPortalFeatureFlag, useFeatureFlag } from "featureflag";

export type ClientReturnsTransferRequestInput = {
    clientServicesReceived: string | null;
    // requestReason: string;
    requestInitiator: string | null;
    familyNotifiedOfTransferNeed: string | null;
    clinicalRecommendationChanging: string | null;
    clinicalRecommendationChange: string | null;
    describeAttemptsMadeToResolveIssue: string | null;
    dateOfLastService: string | null;
    dateOfLastFullScheduleOffer: string | null;
    serviceProvidedDuringTransfer: string | null;
    canContinueProvidingServices: string | null;
    clientReturnDate: string | null;
    offeredParaprofessionalHours: number | null;
    offeredMidLevelHours: number | null;
    offeredHighLevelHours: number | null;
    offeredGroupCareHours: number | null;
    reasonForRequest: string | null;
    assessmentAppointmentOfferedDate: string | null;
    wasFsoOffered: string | null;
    serviceType: string[];
    rationale: string | null;
    paraprofessionalLocation: string | null;
    midLevelLocation: string | null;
    highLevelLocation: string | null;
    groupCareLocation: string | null;
    assessmentLocation: string | null;
} & ClientReportSubmitClinicalRecommendationHourInput;


type Props = {
    visible$: Subject<boolean>;
    authorization: AuthorizationInfo;
    onFinished: (formValues: ClientReturnsTransferRequestInput | null, isDraft: boolean) => Promise<void>;
}

export function ClientTransferReturnRequestFormAuthModal({
                                                             visible$,
                                                             authorization,
                                                             onFinished,
                                                         }: Props) {
    const visible = useObservable(visible$, false);
    const typeOfAuth = isTreatmentAuth(authorization) ? 'treatment' : isAssessmentAuth(authorization) ? 'assessment' : 'transfer-re';
    const isUpdatedTransferRequestDisabled = useFeatureFlag(ProviderPortalFeatureFlag.TDTL_9950_UPDATED_TRANSFER_REQUEST_DISABLED);
    // Only treatment auths pre-populate the Clinical Recommendation section;
    // for assessment/transfer-re auths the fields start empty and are user-entered.
    const defaultClinicalRecommendationFromAuth = useMemo(() => (
        isTreatmentAuth(authorization)
            ? { serviceType: defaultServiceTypesByAuthTier1(authorization) }
            : undefined
    ), [authorization])
    const clinicalRecommendationHoursFromAuth = useMemo(() => (
        isTreatmentAuth(authorization) ? defaultClinicalRecommendationHoursFromAuth(authorization) : undefined
    ), [authorization]);

    // >>> LEGACY (TDTL-9950): master computed these hour/serviceType defaults
    // directly from `authorization`, without the fallback-to-older-fields
    // chain that `defaultClinicalRecommendationHoursFromAuth` added. Delete
    // this block (and the ternary below that references it) when the flag
    // is retired.
    const legacyClinicalRecommendationDefaults = useMemo(() => ({
        serviceType: defaultServiceTypesByAuthTier1(authorization),
        caregiverMidLevelHoursMin: authorization.careMediatedMidMinHours ?? undefined,
        caregiverHighLevelHoursMin: authorization.careMediatedHighMinHours ?? undefined,
        caregiverMidLevelHoursMax: authorization.careMediatedMidHours,
        caregiverHighLevelHoursMax: authorization.careMediatedHighHours,
        practitionerParaprofessionalHoursMin: authorization.practDirectBeneficialHours,
        practitionerParaprofessionalHoursMax: authorization.practDirectOptimalHours,
        practitionerMidLevelHoursMin: authorization.practMidBeneficialHours,
        practitionerMidLevelHoursMax: authorization.practMidOptimalHours,
        practitionerHighLevelHoursMin: authorization.practHighBeneficialHours,
        practitionerHighLevelHoursMax: authorization.practHighOptimalHours,
        practitionerGroupCareHoursMin: authorization.practGroupBeneficialHours,
        practitionerGroupCareHoursMax: authorization.practGroupOptimalHours,
    }), [authorization]);
    // <<< LEGACY (TDTL-9950)

    const methods = useForm<ClientReturnsTransferRequestInput>({
        defaultValues: {
            clientServicesReceived: null,
            clinicalRecommendationChange: null,
            // requestReason: "",
            requestInitiator: null,
            familyNotifiedOfTransferNeed: null,
            describeAttemptsMadeToResolveIssue: null,
            dateOfLastService: null,
            dateOfLastFullScheduleOffer: null,
            serviceProvidedDuringTransfer: null,
            canContinueProvidingServices: null,
            clientReturnDate: null,
            offeredParaprofessionalHours: null,
            offeredMidLevelHours: null,
            offeredHighLevelHours: null,
            offeredGroupCareHours: null,

            serviceType: [],
            reasonForRequest: null,
            assessmentAppointmentOfferedDate: null,
            wasFsoOffered: null,
            rationale: null,
            paraprofessionalLocation: null,
            midLevelLocation: null,
            highLevelLocation: null,
            // TDTL-9950: legacy path uses master's simpler auth-hours formula.
            ...(isUpdatedTransferRequestDisabled ? legacyClinicalRecommendationDefaults : defaultClinicalRecommendationFromAuth)
        }
    });

    // TDTL-9950: master never fetched/prefilled from a previously saved
    // draft - skip the fetch entirely in legacy mode by passing `showModal
    // = false`, so this hook's effects never fire.
    const {isRunning$} = useLatestClientReturnsTransferInput(visible && !isUpdatedTransferRequestDisabled, methods, authorization.guid);
    const loading = useObservable(isRunning$, false);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Client Returns/Transfer is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);

    return (
        <ReportModal visible={visible} title={'Client Returns/Transfer'} onClose={onCancel} maxWidth="max-w-4xl">
            <ClientReturnsTransferForm
                methods={methods}
                visible$={visible$}
                onFinished={onFinished}
                onCancel={onCancel}
                externalLoading={loading}
                recordAuthType={typeOfAuth}
                authorizationGuid={authorization.guid}
                restrictedServiceTypeOpts={serviceTypeOptionsAvailableForAllTiers(authorization)?.options ?? null}
                defaultClinicalRecommendationFromAuth={defaultClinicalRecommendationFromAuth}
                clinicalRecommendationHoursFromAuth={clinicalRecommendationHoursFromAuth}
            />
        </ReportModal>
    )
}